import React, {createContext, useContext, useMemo} from 'react';

import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {SkillChatMode} from 'llm/chat-library/skills/SkillActionsMenu';
import {canManageSkillAccess, downloadSkillBundle, SkillSummary} from 'llm/chat-library/skills/SkillsDAO';
import EntityStore from 'stores/EntityStore';
import type DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import type {
  MantineOverflowMenuOption,
  MantineOverflowMenuSection,
} from 'ui-library/menus/MantineOverflowMenu';

/** Handlers for skill overflow menu actions in the chat settings redesign. */
export interface SkillOverflowMenuHandlers {
  onTryWithChat: (skill: SkillSummary, mode: SkillChatMode) => void;
  onManageAccess: (skill: SkillSummary) => void;
  onArchive: (skill: SkillSummary) => void;
}

const SkillOverflowMenuContext = createContext<SkillOverflowMenuHandlers | null>(null);

export function SkillOverflowMenuProvider({
  handlers,
  children,
}: {
  handlers: SkillOverflowMenuHandlers;
  children: React.ReactNode;
}): JSX.Element {
  const value = useMemo(() => handlers, [handlers]);
  return <SkillOverflowMenuContext.Provider value={value}>{children}</SkillOverflowMenuContext.Provider>;
}

/** Returns redesign overflow-menu handlers when rendered inside {@link SkillOverflowMenuProvider}. */
export function useSkillOverflowMenuHandlers(): SkillOverflowMenuHandlers | null {
  return useContext(SkillOverflowMenuContext);
}

function menuItemTestProps(
  testComponentName: string | undefined,
  element: string
): DataTestAttrs | undefined {
  return testComponentName != null ? {component: testComponentName, element} : undefined;
}

/**
 * Builds the flat overflow menu for a skill. Mirrors {@link SkillActionsMenu} so a skill surfaces
 * the same actions on the redesign listing and detail views.
 */
export function buildSkillOverflowMenuSections(
  skill: SkillSummary,
  handlers: SkillOverflowMenuHandlers,
  options?: {testComponentName?: string}
): Array<MantineOverflowMenuSection> {
  const testComponentName = options?.testComponentName;
  const isTenantAdmin = EntityStore.currentUser()?.isTenantAdmin ?? false;
  const canArchive = skill.extensionDefinitionId == null && (skill.canManageAccess || isTenantAdmin);
  const canEditWithChat = skill.canEdit;
  const canManageSharing = canManageSkillAccess(skill);

  const items: Array<MantineOverflowMenuOption> = [
    {
      name: 'Try with chat',
      dataTestAttrs: menuItemTestProps(testComponentName, 'tryWithChatItem'),
      onSelect: () => {
        handlers.onTryWithChat(skill, SkillChatMode.TRY);
      },
    },
  ];
  if (canEditWithChat) {
    items.push({
      name: 'Edit with chat',
      dataTestAttrs: menuItemTestProps(testComponentName, 'editWithChatItem'),
      onSelect: () => {
        handlers.onTryWithChat(skill, SkillChatMode.EDIT);
      },
    });
  }
  items.push({
    name: 'Download skill',
    dataTestAttrs: menuItemTestProps(testComponentName, 'downloadSkillItem'),
    onSelect: () => {
      downloadSkillBundle(skill.id);
    },
  });
  if (canManageSharing) {
    items.push({
      name: 'Manage access',
      dataTestAttrs: menuItemTestProps(testComponentName, 'manageAccessItem'),
      onSelect: () => {
        handlers.onManageAccess(skill);
      },
    });
  }
  if (canArchive) {
    items.push({
      name: 'Archive',
      variant: 'destructive',
      dataTestAttrs: menuItemTestProps(testComponentName, 'archiveSkillItem'),
      onSelect: () => {
        handlers.onArchive(skill);
      },
    });
  }
  items.push({
    name: EXPORT_AUDIT_LOG_DISPLAY_STRING,
    dataTestAttrs: menuItemTestProps(testComponentName, 'exportAuditLogItem'),
    onSelect: () => {
      exportAuditLogs([skill.id]);
    },
  });

  return [{items}];
}
